import { useCallback, useMemo, useState } from 'react'

/**
 * Walk a cursor-paged feed, remembering how to get back.
 *
 * A cursor feed only ever hands out a token for the *next* page, so going
 * backwards is only possible if the tokens already used are kept. This holds
 * that stack: index 0 is `undefined` (the first page needs no cursor), and each
 * `next()` pushes the cursor the current page returned.
 *
 * `reset` is not optional housekeeping — a cursor encodes a position at a given
 * page size, under a given filter. Change either and every stored cursor becomes
 * meaningless, so the caller must reset when page size or search changes, or
 * page 2 will answer with rows from an older query's position.
 */
export function useCursorPages() {
  /** Cursor per visited page; `[undefined]` is page 1. */
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined])
  const [index, setIndex] = useState(0)

  const next = useCallback(
    (nextCursor: string | undefined) => {
      if (!nextCursor) return
      setCursors((stack) => {
        const ahead = stack.slice(0, index + 1)
        // Re-walking forward after going back must not duplicate the entry.
        return ahead.length > index + 1 ? stack : [...ahead, nextCursor]
      })
      setIndex((i) => i + 1)
    },
    [index],
  )

  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), [])

  const reset = useCallback(() => {
    setCursors([undefined])
    setIndex(0)
  }, [])

  return useMemo(
    () => ({
      /** Cursor for the page to request; `undefined` on the first page. */
      cursor: cursors[index],
      /** 1-based, for display. */
      page: index + 1,
      next,
      prev,
      reset,
    }),
    [cursors, index, next, prev, reset],
  )
}
